import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Award, 
  Target, 
  TrendingUp, 
  Users, 
  PlayCircle,
  CheckCircle,
  Medal,
  BookOpen,
  Briefcase,
  Sparkles,
  ChevronRight,
  Quote
} from 'lucide-react';

const About = () => {
  return (
    <div className="relative overflow-hidden bg-white dark:bg-gray-950">
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 opacity-50"></div>
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-br from-indigo-200/20 to-purple-200/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-indigo-200/20 to-purple-200/20 rounded-full blur-3xl"></div>
      
      <main className="relative">
        {/* Hero Section - Nossa História */}
        <div className="pt-20 pb-16 sm:pt-24 sm:pb-20 lg:pt-32 lg:pb-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6 shadow-lg">
                <Sparkles className="h-4 w-4" />
                <span>Excelência em Marketing Estratégico</span>
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-gray-900 dark:text-white mb-6">
                Transformamos <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">negócios</span> através de estratégias digitais
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Na AURA AI, combinamos expertise técnica com visão estratégica para criar soluções de marketing que realmente geram resultados. Somos especialistas em anúncios, gestão de tráfego e posicionamento de marca.
              </p>
            </div>
          </div>
        </div>

        {/* Nossa Equipe Section */}
        <div className="py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Liderança que <span className="text-indigo-600">transforma</span>
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Conheça os especialistas por trás das estratégias que impulsionam negócios
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Auro Oliveira */}
              <div className="group relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl opacity-20 blur-xl group-hover:opacity-30 transition duration-1000"></div>
                <div className="relative bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden">
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="relative h-80 md:h-full overflow-hidden bg-gradient-to-br from-indigo-600 to-purple-600">
                      <img
                        className="w-full h-full object-cover object-center opacity-90 mix-blend-overlay"
                        src="https://drive.google.com/thumbnail?id=1aHRkHPLwRFoNO6JHXoKLAqVsnTm4BHCx&sz=w1000"
                        alt="Auro Oliveira"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-indigo-900 via-indigo-900/50 to-transparent"></div>
                      <div className="absolute bottom-6 left-6 right-6">
                        <h3 className="text-3xl font-bold text-white mb-2">Auro Oliveira</h3>
                        <p className="text-indigo-200 text-lg">CEO & Estrategista de Marketing</p>
                      </div>
                    </div>
                    <div className="p-8">
                      <div className="flex items-center gap-2 mb-6">
                        <Medal className="h-6 w-6 text-indigo-600" />
                        <span className="text-sm font-semibold text-indigo-600 uppercase tracking-wider">MBA em Marketing Internacional</span>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 dark:text-gray-300">Certificação em Rede de Pesquisa do Google Ads</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 dark:text-gray-300">Fundamentos de Marketing Digital pelo Google</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 dark:text-gray-300">Gerenciador de Negócios pelo Meta</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 dark:text-gray-300">Pré-Mestrado Internacional em Inteligência Artificial</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 dark:text-gray-300">Pós-Graduação em Gestão Comercial e Estratégica em Marketing Digital</span>
                        </div>
                      </div>

                      <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-gray-600 dark:text-gray-400 italic">
                          "Estratégia sem execução é apenas um sonho. Execução sem estratégia é apenas um passatempo. Nosso trabalho é unir ambos para criar resultados extraordinários."
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Izabella Azevedo */}
              <div className="group relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl opacity-20 blur-xl group-hover:opacity-30 transition duration-1000"></div>
                <div className="relative bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden">
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="relative h-80 md:h-full overflow-hidden bg-gradient-to-br from-purple-600 to-pink-600">
                      <img
                        className="w-full h-full object-cover object-center opacity-90 mix-blend-overlay"
                        src="https://drive.google.com/thumbnail?id=1vgf8I5LNLUz8adqQ087DTBO7yH0VpFds&sz=w1000"
                        alt="Izabella Azevedo"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-purple-900 via-purple-900/50 to-transparent"></div>
                      <div className="absolute bottom-6 left-6 right-6">
                        <h3 className="text-3xl font-bold text-white mb-2">Izabella Azevedo</h3>
                        <p className="text-purple-200 text-lg">CMO & Diretora de Conteúdo</p>
                      </div>
                    </div>
                    <div className="p-8">
                      <div className="flex items-center gap-2 mb-6">
                        <Award className="h-6 w-6 text-purple-600" />
                        <span className="text-sm font-semibold text-purple-600 uppercase tracking-wider">Especialista em Conteúdo</span>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 dark:text-gray-300">Direção de Arte para Marketing Digital</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 dark:text-gray-300">Edição Avançada de Vídeos</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 dark:text-gray-300">Criação de Conteúdo Estratégico</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 dark:text-gray-300">Motion Design</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 dark:text-gray-300">Estratégia de Branding Visual</span>
                        </div>
                      </div>

                      <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-gray-600 dark:text-gray-400 italic">
                          "Cada imagem, cada vídeo, cada palavra conta uma história. Meu trabalho é garantir que essa história seja inesquecível e converta."
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Nossa Expertise Section */}
        <div className="py-16 sm:py-24 bg-gradient-to-br from-indigo-600 to-purple-600 relative overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2FSvg%3E')]"></div>
          
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">
                Expertise que <span className="text-yellow-300">gera resultados</span>
              </h2>
              <p className="text-xl text-indigo-100 max-w-3xl mx-auto">
                Combinamos estratégia, criatividade e tecnologia para criar campanhas de alto impacto
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition duration-300">
                <Target className="h-12 w-12 text-yellow-300 mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Anúncios</h3>
                <p className="text-indigo-100">Estratégias pagas com ROI mensurável e otimização contínua</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition duration-300">
                <TrendingUp className="h-12 w-12 text-yellow-300 mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Gestão de Tráfego</h3>
                <p className="text-indigo-100">Otimização de campanhas para máximo desempenho</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition duration-300">
                <Briefcase className="h-12 w-12 text-yellow-300 mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Posicionamento</h3>
                <p className="text-indigo-100">Construção de marca forte e autoridade no mercado</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition duration-300">
                <Sparkles className="h-12 w-12 text-yellow-300 mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Marketing Estratégico</h3>
                <p className="text-indigo-100">Planejamento completo para crescimento sustentável</p>
              </div>
            </div>
          </div>
        </div>

        {/* Certificações e Diferenciais */}
        <div className="py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <BookOpen className="h-4 w-4" />
                  <span>Certificações Internacionais</span>
                </div>
                <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                  Qualificação que <span className="text-indigo-600">faz a diferença</span>
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
                  Nossa equipe possui certificações das maiores plataformas de marketing do mundo, garantindo que suas campanhas estejam sempre à frente da concorrência.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center">
                      <img src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" alt="Google" className="h-6 object-contain" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">Google Ads Certified</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Rede de Pesquisa e Display</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/ab/Meta-Logo.png/1280px-Meta-Logo.png" alt="Meta" className="h-6 object-contain" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">Meta Certified</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Gerenciador de Negócios</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl opacity-20 blur-2xl"></div>
                <div className="relative bg-white dark:bg-gray-900 rounded-3xl shadow-2xl p-8">
                  <Quote className="h-12 w-12 text-indigo-600 opacity-20 absolute top-6 right-6" />
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Nossa Filosofia</h3>
                  <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
                    "Não acreditamos em fórmulas mágicas. Acreditamos em estratégia baseada em dados, criatividade orientada a resultados e parcerias de longo prazo com nossos clientes. Cada negócio é único, e cada solução que criamos é personalizada para atender às necessidades específicas de cada cliente."
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full"></div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">Equipe AURA AI</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Estratégia e Criatividade</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-16 sm:py-24 bg-gray-50 dark:bg-gray-900">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Pronto para <span className="text-indigo-600">transformar</span> seus resultados?
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
              Descubra como nossa expertise pode impulsionar seu negócio com estratégias personalizadas de marketing digital
            </p>
            <Link
              to={`https://wa.me/5534996687855?text=Olá, tenho interesse em agendar!`}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-xl"
            >
              Agendar Consultoria Estratégica
              <ChevronRight className="h-5 w-5" />
            </Link>
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Análise inicial gratuita para entender seu negócio
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default About;